import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "../lib/api";
import { useAuth, isStaff } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function LoginPage() {
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ name: "", email: "", password: "", consent: false });
    const [state, setState] = useState({ loading: false, error: "", info: "" });
    const { setUser } = useAuth();
    const navigate = useNavigate();

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const googleLogin = () => {
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const afterAuth = (user) => {
        setUser(user);
        navigate(isStaff(user) ? "/admin" : "/mon-compte");
    };

    const submit = async (e) => {
        e.preventDefault();
        setState({ loading: true, error: "", info: "" });
        try {
            const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
            const payload = mode === "login" ? { email: form.email, password: form.password } : form;
            const { data } = await api.post(endpoint, payload);
            afterAuth(data);
        } catch (err) {
            setState({ loading: false, error: apiError(err), info: "" });
        }
    };

    const forgot = async () => {
        if (!form.email) {
            setState({ ...state, error: "Indiquez votre e-mail pour recevoir un lien de réinitialisation." });
            return;
        }
        try {
            const { data } = await api.post("/auth/forgot-password", { email: form.email });
            setState({ loading: false, error: "", info: data.detail });
        } catch (err) {
            setState({ loading: false, error: apiError(err), info: "" });
        }
    };

    return (
        <main className="min-h-screen bg-ivory flex items-center justify-center p-6 relative overflow-hidden" data-testid="login-page">
            <div className="absolute -top-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
                <div className="flex justify-center mb-8">
                    <Link to="/" aria-label="Retour à l'accueil"><Logo /></Link>
                </div>
                <div className="card-light p-8">
                    <div className="flex rounded-full bg-pearl p-1 mb-7" role="tablist">
                        {["login", "register"].map((m) => (
                            <button
                                key={m}
                                role="tab"
                                aria-selected={mode === m}
                                onClick={() => { setMode(m); setState({ loading: false, error: "", info: "" }); }}
                                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-forest text-ivory" : "text-ink-soft"}`}
                                data-testid={`tab-${m}`}
                            >
                                {m === "login" ? "Connexion" : "Créer un compte"}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={submit} className="space-y-4">
                        {mode === "register" && (
                            <div>
                                <Label htmlFor="name">Nom</Label>
                                <Input id="name" data-testid="register-name-input" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="email">E-mail</Label>
                            <Input id="email" type="email" data-testid="login-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div>
                            <Label htmlFor="password">Mot de passe {mode === "register" && <span className="text-ink-soft text-xs">(10 caractères minimum)</span>}</Label>
                            <Input id="password" type="password" data-testid="login-password-input" required minLength={mode === "register" ? 10 : 1} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </div>
                        {mode === "register" && (
                            <label className="flex items-start gap-2 text-sm text-ink-soft">
                                <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="register-consent" />
                                J'accepte la politique de confidentialité du site.
                            </label>
                        )}
                        {state.error && <p className="text-sm text-destructive" data-testid="login-error">{state.error}</p>}
                        {state.info && <p className="text-sm text-forest" data-testid="login-info">{state.info}</p>}
                        <Button type="submit" disabled={state.loading} className="w-full bg-forest text-ivory hover:bg-forest-surface font-semibold" data-testid="login-submit-button">
                            {state.loading ? "Patiente…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
                        </Button>
                    </form>
                    {mode === "login" && (
                        <button onClick={forgot} className="mt-3 text-xs text-ink-soft hover:text-gold-dark underline underline-offset-2" data-testid="forgot-password-link">
                            Mot de passe oublié ?
                        </button>
                    )}
                    <div className="mt-6 space-y-3">
                        <div className="flex items-center gap-3 text-ink-soft text-xs">
                            <span className="flex-1 h-px bg-border" /> ou <span className="flex-1 h-px bg-border" />
                        </div>
                        <Button type="button" variant="outline" onClick={googleLogin} className="w-full border-border text-ink hover:bg-pearl gap-2" data-testid="google-login-button">
                            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#EA4335" d="M12 5.04c1.7 0 2.87.74 3.53 1.35l2.58-2.52C16.55 2.4 14.5 1.5 12 1.5 7.62 1.5 3.9 4.02 2.13 7.6l3 2.33C5.86 7.14 8.66 5.04 12 5.04z"/><path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58l3.68 2.85c2.15-1.99 3.75-4.92 3.75-8.67z"/><path fill="#FBBC05" d="M5.13 14.07A7.4 7.4 0 0 1 4.74 12c0-.72.14-1.42.39-2.07l-3-2.33A10.5 10.5 0 0 0 1.5 12c0 1.69.41 3.29 1.13 4.72l2.5-2.65z" opacity=".9"/><path fill="#34A853" d="M12 22.5c2.43 0 4.47-.8 5.96-2.18l-3.68-2.85c-.99.66-2.26 1.06-3.78 1.06-3.34 0-6.14-2.1-6.87-4.94l-3 2.33c1.77 3.58 5.49 6.58 9.37 6.58z" opacity=".9"/></svg>
                            Continuer avec Google
                        </Button>
                        <div className="relative">
                            <Button type="button" variant="outline" disabled className="w-full border-border text-ink-soft/50 gap-2 cursor-not-allowed" data-testid="apple-login-button">
                                 Continuer avec Apple
                            </Button>
                            <p className="text-[11px] text-ink-soft/60 mt-1.5 text-center" data-testid="apple-status">Non configuré — en attente des identifiants Apple Developer</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        </main>
    );
}
